import { useQueryClient } from '@tanstack/react-query';
import {
  useLeadCrudServiceGetApiLeadContactsById,
  useLeadCrudServicePostApiLeadContacts,
  useLeadCrudServicePutApiLeadContactsById,
  useLeadCrudServiceDeleteApiLeadContactsById,
  useAttributesServiceDeleteApiAttributesObjectByObjectTypeByObjectId
} from '../api/generated/queries/queries';
import { UseLeadCrudServiceGetApiLeadContactsByIdKeyFn } from '../api/generated/queries/common';
import { LeadDTO } from '../api/generated/requests/types.gen';
import { useSnackbar } from 'notistack';



/**
 * Hook do pobierania konkretnego kontaktu po ID z cache'owaniem
 * Używa React Query do cache'owania danych
 */
export const useContactById = (id: number) => {
  return useLeadCrudServiceGetApiLeadContactsById({ id }, [], {
    staleTime: 0, // Always consider data stale to ensure fresh fetch
    gcTime: 5 * 60 * 1000, // 5 minutes in cache (reduced from 30 minutes)
    refetchOnWindowFocus: false, // Nie odświeżaj automatycznie przy focus
    refetchOnMount: true, // Zawsze odśwież przy mount komponentu
    enabled: !!id, // Włącz tylko gdy id jest dostępne
    // Force refetch when query key changes (when different contact ID is selected)
    refetchOnReconnect: true,
    retry: false, // WYŁĄCZ retry - nie ponawiaj żadnych zapytań
  });
};

/**
 * Hook do mutacji kontaktów z automatyczną aktualizacją cache
 */
export const useContactMutations = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Funkcja do aktualizacji kontaktu w cache bez ponownego pobierania
  const updateContactInCache = (contactId: number, updates: Partial<LeadDTO>) => {
    // Aktualizuj cache dla wszystkich kontaktów
    queryClient.setQueryData(['LeadCrudServiceGetApiLeadContacts'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.map((contact: any) => 
        contact.id === contactId ? { ...contact, ...updates } : contact
      );
    });

    // Aktualizuj cache dla konkretnego kontaktu
    queryClient.setQueryData(['LeadCrudServiceGetApiLeadContactsById', { id: contactId }], (oldData: any) => {
      if (!oldData) return oldData;
      return { ...oldData, ...updates };
    });
  };

  // Funkcja do dodania kontaktu do cache
  const addContactToCache = (newContact: LeadDTO) => {
    queryClient.setQueryData(['LeadCrudServiceGetApiLeadContacts'], (oldData: any) => {
      if (!oldData) return [newContact];
      return [newContact, ...oldData];
    });
  };

  // Funkcja do usunięcia kontaktu z cache
  const removeContactFromCache = (contactId: number) => {
    // Usuń z cache wszystkich kontaktów
    queryClient.setQueryData(['LeadCrudServiceGetApiLeadContacts'], (oldData: any) => {
      if (!oldData) return oldData;
      return oldData.filter((contact: any) => contact.id !== contactId);
    });

    // Usuń z cache konkretnego kontaktu
    queryClient.removeQueries({ queryKey: ['LeadCrudServiceGetApiLeadContactsById', { id: contactId }] });
  };

  // Mutation do tworzenia kontaktu
  const createContactMutation = useLeadCrudServicePostApiLeadContacts({
    onSuccess: (data) => {
      addContactToCache(data);
      enqueueSnackbar('Kontakt został utworzony', { variant: 'success' });
      // Invalidate paginated query to refresh the table
      queryClient.invalidateQueries({ queryKey: ['LeadCrudServiceGetApiLeadContactsPaginated'] });
    },
    onError: (error) => {
      console.error('Błąd podczas tworzenia kontaktu:', error);
      enqueueSnackbar('Nie udało się utworzyć kontaktu', { variant: 'error' });
    }
  });

  // Mutation do aktualizacji kontaktu z optymistyczną aktualizacją
  const updateContactMutation = useLeadCrudServicePutApiLeadContactsById({
    onMutate: async (variables) => {
      // Anuluj wszystkie wychodzące zapytania dla kontaktów
      await queryClient.cancelQueries({ queryKey: ['LeadCrudServiceGetApiLeadContacts'] });
      await queryClient.cancelQueries({ 
        queryKey: UseLeadCrudServiceGetApiLeadContactsByIdKeyFn({ id: variables.id }, [])
      });

      // Zapisz poprzednie dane na wypadek rollback
      const previousContacts = queryClient.getQueryData(['LeadCrudServiceGetApiLeadContacts']);
      const previousContact = queryClient.getQueryData(
        UseLeadCrudServiceGetApiLeadContactsByIdKeyFn({ id: variables.id }, [])
      );

      // Optymistycznie zaktualizuj cache
      updateContactInCache(variables.id, variables.requestBody);

      // Zwróć kontekst z poprzednimi danymi
      return { previousContacts, previousContact };
    },
    onSuccess: (data, variables) => {
      // Zaktualizuj cache z rzeczywistymi danymi z serwera
      updateContactInCache(variables.id, data);
      enqueueSnackbar('Kontakt został zaktualizowany', { variant: 'success' });
    },
    onError: (error, variables, context) => {
      // Przywróć poprzednie dane w przypadku błędu
      if (context?.previousContacts) {
        queryClient.setQueryData(['LeadCrudServiceGetApiLeadContacts'], context.previousContacts);
      }
      if (context?.previousContact) {
        queryClient.setQueryData(
          UseLeadCrudServiceGetApiLeadContactsByIdKeyFn({ id: variables.id }, []),
          context.previousContact
        );
      }

      console.error('Błąd podczas aktualizacji kontaktu:', error);
      enqueueSnackbar('Nie udało się zaktualizować kontaktu', { variant: 'error' });
    },
    onSettled: (data, error, variables) => {
      // Zawsze odśwież dane po zakończeniu mutacji (sukces lub błąd)
      queryClient.invalidateQueries({ queryKey: ['LeadCrudServiceGetApiLeadContacts'] });
      queryClient.invalidateQueries({ 
        queryKey: UseLeadCrudServiceGetApiLeadContactsByIdKeyFn({ id: variables.id }, [])
      });
      // Invalidate paginated query to refresh the table
      queryClient.invalidateQueries({ queryKey: ['LeadCrudServiceGetApiLeadContactsPaginated'] });
    }
  });

  // Mutation do usuwania atrybutów
  const deleteAttributesMutation = useAttributesServiceDeleteApiAttributesObjectByObjectTypeByObjectId();

  // Mutation do usuwania kontaktu
  const deleteContactMutation = useLeadCrudServiceDeleteApiLeadContactsById({
    onSuccess: async (data, variables) => {
      try {
        // Delete associated attributes first
        await deleteAttributesMutation.mutateAsync({
          objectType: 'LEAD',
          objectId: variables.id
        });
      } catch (error) {
        console.warn('Błąd podczas usuwania atrybutów kontaktu:', error);
        // Continue with contact deletion even if attribute deletion fails
      }
      
      removeContactFromCache(variables.id);
      enqueueSnackbar('Kontakt został usunięty', { variant: 'success' });
      // Invalidate paginated query to refresh the table
      queryClient.invalidateQueries({ queryKey: ['LeadCrudServiceGetApiLeadContactsPaginated'] });
    },
    onError: (error) => {
      console.error('Błąd podczas usuwania kontaktu:', error);
      enqueueSnackbar('Nie udało się usunąć kontaktu', { variant: 'error' });
    }
  });

  return {
    createContact: createContactMutation.mutateAsync,
    updateContact: updateContactMutation.mutateAsync,
    deleteContact: deleteContactMutation.mutate,

    // Stany mutacji
    isCreating: createContactMutation.isPending,
    isUpdating: updateContactMutation.isPending,
    isDeleting: deleteContactMutation.isPending,
  };
};
